import { publicApi } from './public-client';
import { User } from '../interfaces/user-interfaces';
import { Onboarding } from '../interfaces/onboarding-interfaces';

/**
 * LOBBERO STAYS SIGN-UP (Independent Stays plan IS3).
 * lobbero_be/src/stays/signup.js: GET /signup/stays/config, POST /signup/stays.
 * Public calls: no session yet, so they go through publicApi.
 */

export interface StaysSignupLegal {
  terms_version: string;
  dpa_version: string;
  terms_url: string;
  dpa_url: string;
}

export interface StaysSignupConfig {
  /** 'closed' until the owner opens sign-up (STAYS_SIGNUP=open). */
  mode: 'closed' | 'open';
  /** Stays billing (IS4): when true the page asks for a plan (and a card unless free_signup). */
  billing: boolean;
  /** FREE_SIGNUP on the server: the plan is complimentary, no card step. */
  free_signup?: boolean;
  plans: StaysSignupPlan[];
  /** Read from the Stripe price; null when none carries a trial. */
  trial_days: number | null;
  /** The most places to stay one sign-up may ask for. */
  max_properties?: number;
  legal: StaysSignupLegal;
  payouts: boolean;
  /** S2 (IS8): the signed time token the sign-up must bring back (lobbero_be/src/stays/bot-check.js). */
  form_token?: string;
}

/** A Stays plan offered at sign-up (IS4). Every amount is the server's, in cents. */
export interface StaysSignupPlan {
  code: 'stays_listing' | 'stays_checkin';
  name: string;
  description: string;
  /** Per place to stay per month (0 on the per-check-in plan). */
  price_cents: number;
  currency: string;
  model: 'per_listing' | 'per_checkin';
  /** Per completed check-in (the per-check-in plan only). */
  fee_cents: number;
}

export interface StaysSignupBody {
  first_name: string;
  last_name?: string;
  email: string;
  password: string;
  phone_number?: string;
  business_name?: string;
  accept: { terms: boolean; dpa: boolean; terms_version: string; dpa_version: string };
  /** With billing on (IS4). */
  plan_code?: string;
  properties?: number;
  promo_code?: string;
  /** S2 (IS8): the bot check. form_token from the config; website is the honeypot (a person leaves it empty). */
  form_token?: string;
  website?: string;
}

export interface StaysSignupCreated {
  user: User;
  onboarding: Onboarding | null;
  permissions: string[];
  account_created: true;
  /** Where the new owner goes: the setup guide, step 1 "Your property". */
  next: string;
  /** With billing on (IS4): the SetupIntent for the card step; null under FREE_SIGNUP. */
  payment?: { client_secret: string; subscription_id: string } | null;
  free_signup?: boolean;
}

/** The server's refusal, as the page needs it. */
export interface StaysSignupRefusal {
  status: number;
  code: string | null;
  message: string;
  fields: Record<string, string>;
  missing: string[];
  legal: StaysSignupLegal | null;
  /** FORM_EXPIRED (S2): a fresh token to press again with. */
  form_token: string | null;
}

export const getStaysSignupConfig = async (): Promise<StaysSignupConfig> =>
  (await publicApi.get('/signup/stays/config')).data as StaysSignupConfig;

export const postStaysSignup = async (body: StaysSignupBody): Promise<StaysSignupCreated> =>
  (await publicApi.post('/signup/stays', body)).data as StaysSignupCreated;

/** Join the existing Stays waitlist (lobbero_be/src/waitlist.js). */
export const joinStaysWaitlist = async (email: string): Promise<void> => {
  await publicApi.post('/waitlist/stays', { email, source: 'signup-stays' });
};

interface ErrorLike {
  response?: { status?: number; data?: Record<string, unknown> };
}

export const refusalOf = (err: unknown): StaysSignupRefusal => {
  const r = (err as ErrorLike)?.response;
  const d = (r && r.data) || {};

  return {
    status: (r && r.status) || 0,
    code: typeof d.code === 'string' ? d.code : null,
    message: typeof d.message === 'string' ? d.message : '',
    fields: (d.fields && typeof d.fields === 'object' && !Array.isArray(d.fields) ? d.fields : {}) as Record<string, string>,
    missing: Array.isArray(d.missing) ? (d.missing as string[]) : [],
    legal: (d.legal as StaysSignupLegal) || null,
    form_token: typeof d.form_token === 'string' ? d.form_token : null,
  };
};
